import React, { useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import api from '../../services/api';
import { useApiQuery } from '../../hooks/useApiQuery';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const EntryExit = () => {
  const { eventId } = useOutletContext();

  // ✅ React Query: Fetch session analytics
  const {
    data: analyticsData = {},
    isLoading,
    isError,
  } = useApiQuery(
    ['session-analytics', eventId],
    () =>
      api
        .get('/sessions/analytics', {
          params: {
            eventId,
          },
        })
        .then(res => res.data),
    {
      enabled: Boolean(eventId),
      staleTime: 60 * 1000,
      onError: error => {
        console.error(
          'Failed to fetch entry/exit analytics:',
          error
        );
      },
    }
  );

  // ✅ Process entry/exit data (memoized for performance)
  const { trendData, ratio } = useMemo(() => {
    const hourly = analyticsData?.entryExitHourly || [];

    const trendData = Array.from(
      { length: 24 },
      (_, hour) => {
        const item = hourly.find(
          data => Number(data.hour) === hour
        );

        return {
          hour,
          in: item?.in || 0,
          out: item?.out || 0,
        };
      }
    );

    return {
      trendData,
      ratio: {
        in: analyticsData?.totalIn || 0,
        out: analyticsData?.totalOut || 0,
      },
    };
  }, [analyticsData]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-[#1471b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-[#708090] text-sm font-['Barlow']">Loading entry/exit data...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isError) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Failed to load entry/exit data</p>
        <p className="text-sm mt-2">Please refresh the page or try again later.</p>
      </div>
    );
  }

  // ✅ No data state
  const hasData = trendData.some(h => h.in > 0 || h.out > 0);

  if (!hasData) {
    return (
      <div>
        <h2 className="text-2xl font-bold mb-6 text-[#2b3445]">Entry / Exit Analytics</h2>
        <div className="flex flex-col items-center justify-center py-16 bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)]">
          <svg
            className="w-16 h-16 mb-4 text-[#dae1e7]"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
          <p className="text-[#708090] text-sm">No entry/exit data available for this event</p>
          <p className="text-xs text-[#708090] mt-1">Check back after attendees start checking in</p>
        </div>
      </div>
    );
  }

  // ✅ Pie chart colors
  const COLORS = ['#3B82F6', '#EF4444'];
  const pieData = [
    { name: 'IN', value: ratio.in },
    { name: 'OUT', value: ratio.out },
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 text-[#2b3445]">Entry / Exit Analytics</h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Hourly Trend Chart */}
        <div className="bg-white p-4 rounded-[15px] shadow-sm border border-[#dae1e7]">
          <h3 className="text-lg font-semibold mb-4 text-[#2b3445]">Hourly Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#dae1e7" />
              <XAxis 
                dataKey="hour" 
                tick={{ fill: '#708090', fontSize: 11 }}
                axisLine={{ stroke: '#dae1e7' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fill: '#708090', fontSize: 11 }}
                axisLine={{ stroke: '#dae1e7' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #dae1e7',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
                labelStyle={{ fontWeight: 600 }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="in" 
                stroke="#3B82F6" 
                name="IN" 
                strokeWidth={2}
                dot={{ fill: '#3B82F6' }}
              />
              <Line 
                type="monotone" 
                dataKey="out" 
                stroke="#EF4444" 
                name="OUT" 
                strokeWidth={2}
                dot={{ fill: '#EF4444' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* IN vs OUT Ratio Pie Chart */}
        <div className="bg-white p-4 rounded-[15px] shadow-sm border border-[#dae1e7]">
          <h3 className="text-lg font-semibold mb-4 text-[#2b3445]">IN vs OUT Ratio</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                label={({ name, value }) => `${name}: ${value}`}
                labelLine={true}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #dae1e7',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 mt-6">
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Total IN</p>
          <p className="text-2xl font-bold text-[#3B82F6] mt-1">{ratio.in}</p>
        </div>
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Total OUT</p>
          <p className="text-2xl font-bold text-[#EF4444] mt-1">{ratio.out}</p>
        </div>
        <div className="bg-white rounded-[15px] border border-[#dae1e7] shadow-[0px_1px_3px_0px_rgba(3,0,71,0.09)] p-4 text-center">
          <p className="text-xs font-semibold text-[#708090] uppercase tracking-wide">Currently Inside</p>
          <p className={`text-2xl font-bold mt-1 ${(ratio.in - ratio.out) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {ratio.in - ratio.out}
          </p>
        </div>
      </div>
    </div>
  );
};

export default EntryExit;